import { logger, storage } from '@/shared/utils';
import { useEffect } from 'react';
import { IconArrowLeft, GroupAvatarIcon, IncomingGroupCall } from '@/assets/images';
import { UserProfile, Username } from '@/features/users';
import { useUserDetails } from '@/features/users/model';
import { ExternalBadge } from '@/features/invite/ui';
import styles from './CallConnectionScreen.module.scss';
import { IncomingCallScreen, OutgoingCallScreen } from './components';
import LocalVideoPreview from './LocalVideoPreview';
import { useCallConnectionScreen } from '@/features/calls/viewmodels/useCallConnectionScreen';
import { useLanguage } from '@/localization/useLanguage';

/**
 * Pure rendering component — zero business logic.
 * All derived values (callStatus, statusDisplay, isAudioMuted, etc.) come from useCallConnectionScreen().
 */
export function CallConnectionScreen() {
   const { t } = useLanguage();
   const {
      callScreenStatus,
      remoteUserId,
      groupId,
      groupName,
      ongoingCallParticipants,
      statusDisplay,
      isAudioMuted,
      isVideoMuted,
      localUserId,
      handleBack,
      callType,
   } = useCallConnectionScreen();
   // Only meaningful for 1-1 calls — a group/meet call has no single "external" user.
   const { isExternal } = useUserDetails(!groupId ? remoteUserId : null, ['isExternal']);

   const renderYouTile = () => {
      const isVideoCall = callType === 'VIDEO';
      const showVideo = isVideoCall && !isVideoMuted;
      return (
         <div className={styles.miniVideo}>
            <div className={`${styles.miniVideoContent} ct-flexCenter ct-hFull`}>
               {showVideo ? (
                  <LocalVideoPreview isVideoMuted={isVideoMuted} avatarClassName="profile-avatar chat-avatar-fallback ct-flexCenter" />
               ) : (
                  <UserProfile userId={localUserId} className="profile-avatar chat-avatar-fallback ct-flexCenter" />
               )}
            </div>
            <span className={styles.youLabel}>{t('common.YOU')}</span>
         </div>
      );
   };

   // Audio playback side-effect — lives here because it uses DOM Audio API directly
   useEffect(() => {
      let audio = null;
      if (callScreenStatus === 'outgoing') audio = new Audio('/sounds/outgoingRinging.wav');
      else if (callScreenStatus === 'incoming') audio = new Audio('/sounds/ringtone.wav'); //incomingNew_2026.mp3

      if (audio) {
         audio.loop = true;
         audio.play().catch(err => logger.warn('Auto-play failed:', err));
      }
      return () => {
         if (audio) {
            audio.pause();
            audio.currentTime = 0;
         }
      };
   }, [callScreenStatus]);

   const renderGroupMembersSubtitle = () => {
      if (!ongoingCallParticipants?.length) return null;
      const currentUserName = storage.getItem('username');
      const remoteParticipants = ongoingCallParticipants.filter(p => p.userId !== currentUserName);
      if (remoteParticipants.length <= 2) {
         return remoteParticipants.map((p, i) => (
            <span key={p.userId}>
               <Username userId={p.userId} maxLength={12} />
               {i < remoteParticipants.length - 1 ? ', ' : ''}
            </span>
         ));
      }
      return (
         <>
            <Username userId={remoteParticipants[0]?.userId} maxLength={12} />,{' '}
            <Username userId={remoteParticipants[1]?.userId} maxLength={12} />, +{remoteParticipants.length - 2}{' '}
            {t('common.OTHERS') || 'others'}
         </>
      );
   };

   // ─── Incoming Call Card Layout ─────────────────────────────────────────────
   if (callScreenStatus === 'incoming') {
      return (
         <div className={`${styles.incomingRoot} ct-flex ct-aiCenter ct-jcCenter`}>
            <div className={`${styles.incomingCard} ct-flex ct-fcol ct-aiCenter`}>
               <div className={`${styles.incomingCenterContent} ct-flex ct-fcol ct-aiCenter`}>
                  <div className={`${styles.incomingAvatarContainerOnetoOne} ct-flexCenter`}>
                     <UserProfile
                        userId={groupId || remoteUserId}
                        className={`${styles.userProfile} ct-flexCenter`}
                        defaultIcon={groupId ? <IncomingGroupCall /> : null}
                     />
                  </div>
                  {ongoingCallParticipants?.length > 2 || groupId ? (
                     <>
                        {groupId && <Username userId={groupId} />}
                        <div className={styles.incomingGroupMembers}>{renderGroupMembersSubtitle()}</div>
                     </>
                  ) : (
                     <div className={`${styles.incomingName} ct-flex ct-aiCenter ct-jcCenter ct-gap6`}>
                        <Username userId={remoteUserId} maxLength={12} />
                        <ExternalBadge isExternalUser={isExternal} className="external-badge--compact" />
                     </div>
                  )}
                  <div className={styles.incomingStatus}>{statusDisplay}</div>
               </div>

               <div className={`${styles.incomingControlsBar} ct-flex ct-jcCenter`}>
                  <IncomingCallScreen />
               </div>
            </div>
         </div>
      );
   }

   return (
      <div className={`${styles.root} ct-flex ct-fcol`}>
         <div className={`${styles.topBar} ct-flex ct-aiCenter`}>
            <button className={`${styles.backButton} ct-flexCenter`} onClick={handleBack} aria-label={t('common.BACK')}>
               <i className="ct-icon sm">
                  <IconArrowLeft />
               </i>
            </button>
         </div>

         <div className={`${styles.centerContent} ct-flex ct-fcol ct-aiCenter ct-jcCenter`}>
            <UserProfile
               userId={groupId || remoteUserId}
               className={`${styles.avatarContainer} ct-flexCenter`}
               defaultIcon={groupId ? <GroupAvatarIcon /> : null}
            />
            <div className={`${styles.name} ct-flex ct-fcol ct-aiCenter ct-jcCenter`}>
               {groupName ? (
                  <span className={styles.groupMembersSubtitle}>{groupName}</span>
               ) : (
                  <>
                     <div className="ct-flex ct-aiCenter ct-gap6">
                        <Username userId={groupId || remoteUserId} />
                        {!groupId && <ExternalBadge isExternalUser={isExternal} className="external-badge--compact" />}
                     </div>
                     {(ongoingCallParticipants?.length > 2 || groupId) && (
                        <span className={styles.groupMembersSubtitle}>{renderGroupMembersSubtitle()}</span>
                     )}
                  </>
               )}
            </div>
            <div className={styles.status}>{statusDisplay}</div>
         </div>

         <div className={`${styles.controlsBar} ct-flex ct-aiCenter ct-jcCenter`}>
            <OutgoingCallScreen isAudioMuted={isAudioMuted} isVideoMuted={isVideoMuted} callStatus={statusDisplay} />
         </div>
         {renderYouTile()}
      </div>
   );
}
